/* eslint-disable react-hooks-extra/no-direct-set-state-in-use-effect */
/**
 * WAARSCHUWING:
 *
 * Deze code is echt bizar slecht geschreven en bevat veel onnodige complexiteit.
 * Het is een grote puinhoop en het zou beter zijn om het helemaal opnieuw te schrijven.
 * Deze code is een voorbeeld van hoe het niet moet.
 *
 * Kopieer deze code dan ook nooit.
 * - Copilot 12-08-2025
 *
 */

import type { components } from '@connected/api-client/api.generated';
import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { api } from '@connected/api-client';
import { validators } from '@connected/api-client/generated/validators.generated';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { DateField } from '@connected/form-fields/components/date';
import FieldInfo from '@connected/form-fields/components/field-info';
import { Label } from '@connected/form-fields/components/label';
import {
    MultipleOptionField,
    MultipleOptionItem,
    SingleHorizontalOptionItemWithoutDot,
    SingleOptionField,
    SingleOptionItem,
} from '@connected/form-fields/components/option';
import { SwitchField } from '@connected/form-fields/components/switch';
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '@connected/ui/components/breadcrumb';
import { Button } from '@connected/ui/components/button';
import { FilePreview } from '@connected/ui/components/file-preview';
import { Input } from '@connected/ui/components/input';
import {
    Popout,
    PopoutBody,
    PopoutContent,
    PopoutFooter,
    PopoutHeader,
    PopoutTitle,
    PopoutTrigger,
} from '@connected/ui/components/popout';
import { Separator } from '@connected/ui/components/separator';
import { Table } from '@connected/ui/components/table';
import { DEFAULT_FILE_ORDER } from '@connected/ui/config/file';
import { Container } from '@connected/ui/layouts/container';
import { useForm } from '@tanstack/react-form';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { ArrowDown, ArrowUp, Pen, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { FileField } from '@/components/file';
import { InformationLine } from '@/components/information-line';
import { useFormTouched } from '@/hooks/use-form-touched';
import { queryClient } from '@/lib/query-client';
import { showConfirm } from '@/stores/confirmer';
import { useOffline } from '@/stores/network-status';

function organisationGet({ organisationId }: { organisationId: string }) {
    return api.queryOptions('get', '/api/v2/organizations/edit', {
        params: {
            query: {
                OrganizationId: organisationId,
            },
        },
    });
}

function usersList({ organisationId }: { organisationId: string }) {
    return api.queryOptions('get', '/api/v2/users/list', {
        params: {
            query: {
                OrganizationId: organisationId,
                PageNumber: 1,
                PageSize: 100,
                Select: {
                    Status: true,
                    Description: true,
                },
                Sort: ['Email'],
            },
        },
    });
}

function measurementDevicesCreate({
    organisationId,
}: {
    organisationId: string;
}) {
    return api.queryOptions('get', '/api/v2/electrical/measurementdevices/create', {
        params: {
            query: {
                OrganizationId: organisationId,
            },
        },
    });
}

function organisationRolesGet({ organisationId }: { organisationId: string }) {
    return api.queryOptions('get', '/api/v2/users/organizations/create', {
        params: {
            query: {
                OrganizationId: organisationId,
            },
        },
    });
}

function normsList({ organisationId }: { organisationId: string }) {
    return api.queryOptions('get', '/api/v2/electrical/inspectionnorms/lists/list', {
        params: {
            query: {
                OrganizationId: organisationId,
                PageNumber: 1,
                PageSize: 100,
                Select: {
                    Status: true,
                    Description: true,
                },
                Sort: ['Status'],
            },
        },
    });
}

function observationStandardTexts({
    organisationId,
}: {
    organisationId: string;
}) {
    return api.queryOptions(
        'get',
        '/api/v2/electrical/observationstandardtexts/items/list',
        {
            params: {
                query: {
                    OrganizationId: organisationId,
                    PageNumber: 1,
                    PageSize: 100,
                    Select: {
                        ObjectKind: true,
                        Description: true,
                        Order: true,
                        TextKind: true,
                    },
                    Sort: ['Order'],
                },
            },
        },
    );
}

function checkList({ organisationId }: { organisationId: string }) {
    return api.queryOptions('get', '/api/v2/electrical/checklists/lists/list', {
        params: {
            query: {
                OrganizationId: organisationId,
                PageNumber: 1,
                PageSize: 100,
                Status: {
                    Filters: [
                        {
                            Value: 'Active',
                            ExpressionKind: 'Equal',
                            IsIncludeNullValues: false,
                        },
                    ],
                },
                Select: {
                    Status: true,
                    Description: true,
                },
                Sort: ['Status'],
            },
        },
    });
}

function normsGet({
    electricalInspectionNormListId,
}: {
    electricalInspectionNormListId: string;
}) {
    return api.queryOptions('get', '/api/v2/electrical/inspectionnorms/items/list', {
        params: {
            query: {
                ElectricalInspectionNormListId: electricalInspectionNormListId,
                Select: {
                    Name: true,
                    Description: true,
                    IsCustomInput: true,
                    NormKind: true,
                    Order: true,
                },
                Sort: ['Order'],
                PageNumber: 1,
                PageSize: 100,
            },
        },
    });
}

function checkListItemsGet({
    electricalChecklistListId,
}: {
    electricalChecklistListId: string;
}) {
    return api.queryOptions('get', '/api/v2/electrical/checklists/items/list', {
        params: {
            query: {
                ElectricalCheckListId: electricalChecklistListId,
                Select: {
                    Description: true,
                    Order: true,
                    Kind: true,
                },
                Sort: ['Order'],
                PageNumber: 1,
                PageSize: 100,
            },
        },
    });
}

function checkListItemTextGet({
    electricalChecklistItemId,
}: {
    electricalChecklistItemId: string;
}) {
    return api.queryOptions('get', '/api/v2/electrical/checklists/texts/list', {
        params: {
            query: {
                ElectricalCheckListItemId: electricalChecklistItemId,
                Select: {
                    Text: true,
                    Order: true,
                    Kind: true,
                },
                Sort: ['Order'],
                PageNumber: 1,
                PageSize: 100,
            },
        },
    });
}

function measurementDevices({ organisationId }: { organisationId: string }) {
    return api.queryOptions('get', '/api/v2/electrical/measurementdevices/list', {
        params: {
            query: {
                OrganizationId: organisationId,
                PageNumber: 1,
                PageSize: 100,
                Select: {
                    BrandName: true,
                    Description: true,
                    ModelName: true,
                    Order: true,
                    CalibrationKind: true,
                    CalibrationInterval: true,
                    DeviceType: true,
                    IsCalibrated: true,
                    IsRetired: true,
                    SerialNumber: true,
                    Status: true,
                    Name: true,
                    UserDisplayName: true,
                },
                Sort: ['Name'],
            },
        },
    });
}

function measurementDevicesCalibrations({ measurementDeviceId }: { measurementDeviceId: string }) {
    return api.queryOptions('get', '/api/v2/electrical/measurementdevices/calibrations/list', {
        params: {
            query: {
                ElectricalMeasurementDeviceId: measurementDeviceId,
                PageNumber: 1,
                PageSize: 100,
            },
        },
    });
}

export const Route = createFileRoute(
    '/(auth)/admin/(organisations)/organisation/information/',
)({
    component: Page,
    validateSearch: z.object({
        organisationId: z.string().min(1),
    }),
    loaderDeps: ({ search }) => ({
        organisationId: search.organisationId,
    }),
    loader: ({ context, deps }) => {
        context.queryClient.ensureQueryData(organisationGet(deps));
        context.queryClient.ensureQueryData(usersList(deps));
        context.queryClient.ensureQueryData(organisationRolesGet(deps));
        context.queryClient.ensureQueryData(normsList(deps));
    },
});

function Page() {
    const { organisationId } = Route.useSearch();

    const { data } = useQuery(organisationGet({ organisationId }));

    return (
        <div className="flex flex-col gap-2">
            <Breadcrumb>
                <BreadcrumbList>
                    <BreadcrumbItem>
                        <BreadcrumbLink
                            className="flex items-center gap-2"
                            asChild
                        >
                            <Link to="/admin/organisations">Organisaties</Link>
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbPage>
                            {data?.Organization?.Name}
                        </BreadcrumbPage>
                    </BreadcrumbItem>
                </BreadcrumbList>
            </Breadcrumb>
            <div className="flex flex-col gap-6">
                <div className="flex gap-1">
                    <Container className="w-full">
                        <Container.Content>
                            <OrganizationInfo />
                        </Container.Content>
                    </Container>
                    <Container className="w-full">
                        <Container.Content>
                            <UserInfo />
                        </Container.Content>
                    </Container>
                </div>
                {data?.Organization?.ElectricalOrganizationKinds?.includes('Inspection') && (
                    <>
                        <Container className="w-full">
                            <Container.Content>
                                <ListManagement />
                            </Container.Content>
                        </Container>

                    </>
                )}
            </div>
        </div>
    );
}

type FormValues = {
    name: string;
    description: string;
    slogan: string;
    email: string;
    telephoneNumber: string;
    website: string;
    iBAN: string;
    bIC: string;
    vatNumber: string;
    electricalOrganizationKinds: components['schemas']['ElectricalOrganizationKind'][];
    sciosRegistrationNumber: string;
    enableElectricalProjectPriceQuote: boolean;
    enableElectricalProjectPlanning: boolean;
    enableElectricalProjectRecommendations: boolean;
};

function OrganizationInfo() {
    const { t: a } = useTranslation('admin');

    const isUserOffline = useOffline();

    const { organisationId } = Route.useSearch();

    const { data } = useQuery(organisationGet({ organisationId }));

    const organisationMutation = api.useMutation(
        'put',
        '/api/v2/organizations/edit',
        {
            onSuccess: () => {
                toast.success('Success');
                queryClient.invalidateQueries(
                    organisationGet({ organisationId }),
                );
            },
        },
    );

    const { Field, handleSubmit, Subscribe } = useForm<FormValues, ZodValidator>({
        defaultValues: {
            name: data?.Organization?.Name || '',
            description: data?.Organization?.Description || '',
            slogan: data?.Organization?.Slogan || '',
            email: data?.Organization?.Email || '',
            telephoneNumber: data?.Organization?.TelephoneNumber || '',
            website: data?.Organization?.Website || '',
            iBAN: data?.Organization?.IBAN || '',
            bIC: data?.Organization?.BIC || '',
            vatNumber: data?.Organization?.VatNumber || '',
            electricalOrganizationKinds:
                data?.Organization?.ElectricalOrganizationKinds || [],
            sciosRegistrationNumber:
                data?.Organization?.SciosRegistrationNumber || '',
            enableElectricalProjectPriceQuote: data?.Organization?.EnableElectricalProjectPriceQuote ?? false,
            enableElectricalProjectPlanning:
                data?.Organization?.EnableElectricalProjectPlanning ?? false,
            enableElectricalProjectRecommendations:
                data?.Organization?.EnableElectricalProjectRecommendations
                ?? false,
        },
        validatorAdapter: zodValidator(),
        onSubmit: async ({ value }) => {
            const body: components['schemas']['EditOrganizationCommand'] = {
                OrganizationId: organisationId,
                Name: value.name,
                Description: value.description,
                Slogan: value.slogan,
                TelephoneNumber: value.telephoneNumber,
                IBAN: value.iBAN,
                BIC: value.bIC,
                VatNumber: value.vatNumber,
                ElectricalOrganizationKinds:
                    value.electricalOrganizationKinds,
                SciosRegistrationNumber: value.sciosRegistrationNumber,
                EnableElectricalProjectPriceQuote:
                    value.enableElectricalProjectPriceQuote,
                EnableElectricalProjectPlanning:
                    value.enableElectricalProjectPlanning,
                EnableElectricalProjectRecommendations:
                    value.enableElectricalProjectRecommendations,
            };

            if (value.email) {
                body.Email = value.email;
            }

            if (value.website) {
                body.Website = value.website;
            }

            await organisationMutation.mutateAsync({
                body,
            });
        },
    });

    function submitForm() {
        handleSubmit();
    }

    const deleteImageMutation = api.useMutation('delete', '/api/v2/organizations/files/delete', {
        onSuccess: () => {
            queryClient.invalidateQueries(organisationGet({
                organisationId,
            }));
        },
    });

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
                <Container.Title>{data?.Organization?.Name}</Container.Title>
            </div>
            <div className="flex flex-col gap-2">
                <InformationLine
                    label={a('organisations.organisation.info.chamberOfCommerceNumber')}
                    value={data?.Organization?.BusinessRegistration?.Number}
                />
                {data?.Organization?.BusinessRegistration?.BranchNumber && (
                    <InformationLine
                        label={a('organisations.organisation.info.branchNumber')}
                        value={
                            data?.Organization?.BusinessRegistration
                                ?.BranchNumber
                        }
                    />
                )}
                <InformationLine
                    label={a('organisations.organisation.info.streetAndNumber')}
                    value={
                        `${data?.Organization?.Address?.Street
                        } ${data?.Organization?.Address?.StreetNumber}`
                    }
                />
                <InformationLine
                    label={a('organisations.organisation.info.postalCodeAndCity')}
                    value={
                        `${data?.Organization?.Address?.PostalCode
                        } ${data?.Organization?.Address?.Locality}`
                    }
                />
            </div>
            <Label label={a('organisations.organisation.info.logo')}>
                {!data?.Organization?.LogoFile?.OrganizationFileId
                    ? (
                            <FileField
                                type="organizationLogo"
                                onSuccess={() =>
                                    queryClient.invalidateQueries(organisationGet({
                                        organisationId,
                                    }),
                                    )}
                                options={{
                                    params: {
                                        query: {
                                            Order: DEFAULT_FILE_ORDER,
                                            OrganizationId: organisationId,
                                        },
                                    },
                                }}
                                disabled={isUserOffline}
                            />
                        )
                    : (
                            <div className="flex items-center justify-between gap-2">
                                <FilePreview
                                    queryOptions={elektraAppQueryOptions.files.organizationLogoImage.download({
                                        organizationFileId: data?.Organization?.LogoFile?.OrganizationFileId,
                                        quality: 'Original',
                                        fileId: null,
                                    })}
                                    metaData={data?.Organization?.LogoFile?.File ?? undefined}
                                />
                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() => deleteImageMutation.mutate({
                                        params: {
                                            query: {
                                                OrganizationFileId: data.Organization!.LogoFile!.OrganizationFileId!,
                                            },
                                        },
                                    })}
                                >
                                    <Trash2 />
                                </Button>
                            </div>
                        )}
            </Label>
            <Field
                name="name"
                validators={{
                    onChange: validators.editOrganizationCommand.name,
                }}
            >
                {({ handleChange, state, name }) => (
                    <Label label={a('organisations.organisation.info.name')}>
                        <Input
                            type="text"
                            name={name}
                            onChange={e => handleChange(e.target.value)}
                            value={state.value}
                            placeholder={a('organisations.form.name.placeholder')}
                        />
                        <FieldInfo state={state} />
                    </Label>
                )}
            </Field>
            <Field
                name="email"
                validators={{
                    onChange: validators.editOrganizationCommand.email,
                }}
            >
                {({ handleChange, state, name }) => (
                    <Label label={a('organisations.form.email.label')}>
                        <Input
                            type="text"
                            name={name}
                            onChange={e => handleChange(e.target.value)}
                            value={state.value}
                            placeholder={a(
                                'organisations.form.email.placeholder',
                            )}
                        />
                        <FieldInfo state={state} />
                    </Label>
                )}
            </Field>
            <Field
                name="website"
                validators={{
                    onChange: validators.editOrganizationCommand.website,
                }}
            >
                {({ handleChange, state, name }) => (
                    <Label label={a('organisations.organisation.info.website')}>
                        <Input
                            type="text"
                            name={name}
                            onChange={e => handleChange(e.target.value)}
                            value={state.value}
                            placeholder={a('organisations.form.website.placeholder')}
                        />
                        <FieldInfo state={state} />
                    </Label>
                )}
            </Field>
            <Field
                name="telephoneNumber"
                validators={{
                    onChange:
                        validators.editOrganizationCommand.telephoneNumber,
                }}
            >
                {({ handleChange, state, name }) => (
                    <Label label={a('organisations.organisation.info.phone')}>
                        <Input
                            type="text"
                            name={name}
                            onChange={e => handleChange(e.target.value)}
                            value={state.value}
                            placeholder={a(
                                'organisations.form.phone.placeholder',
                            )}
                        />
                        <FieldInfo state={state} />
                    </Label>
                )}
            </Field>
            <Field
                name="electricalOrganizationKinds"
                validators={{
                    onChange:
                        validators.editOrganizationCommand.electricalOrganizationKinds,
                }}
            >
                {({ handleChange, state, name }) => (
                    <Label label={a('organisations.organisation.info.companyType')}>
                        <MultipleOptionField
                            name={name}
                            handleChange={handleChange}
                            options={[
                                {
                                    label: 'Inspectie',
                                    value: 'Inspection',
                                },
                                {
                                    label: 'None',
                                    value: 'None',
                                },
                            ]}
                            state={state}
                            render={MultipleOptionItem}
                            dropdown
                        />
                        <FieldInfo state={state} />
                    </Label>
                )}
            </Field>
            <Subscribe selector={state => state.values.electricalOrganizationKinds}>
                {electricalOrganizationKinds => (
                    electricalOrganizationKinds.includes('Inspection') && (
                        <div>
                            <Field
                                name="sciosRegistrationNumber"
                                validators={{
                                    onChange:
                                        validators.editOrganizationCommand.sciosRegistrationNumber,
                                }}
                            >
                                {({ handleChange, state, name }) => (
                                    <Label label={a('organisations.organisation.info.sciosRegistration')}>
                                        <Input
                                            type="text"
                                            name={name}
                                            onChange={e => handleChange(e.target.value)}
                                            value={state.value}
                                            placeholder={a('organisations.organisation.info.sciosRegistration')}
                                        />
                                        <FieldInfo state={state} />
                                    </Label>
                                )}
                            </Field>
                            <Field
                                name="enableElectricalProjectPriceQuote"
                                validators={{
                                    onChange:
                                        validators.editOrganizationCommand.enableElectricalProjectPriceQuote,
                                }}
                            >
                                {({ handleChange, state, name }) => (
                                    <>
                                        <SwitchField
                                            name={name}
                                            handleChange={handleChange}
                                            value={state.value}
                                            label={a('organisations.organisation.info.quoteModule')}
                                        />
                                        <FieldInfo state={state} />
                                    </>
                                )}
                            </Field>
                            <Field
                                name="enableElectricalProjectPlanning"
                                validators={{
                                    onChange:
                                        validators.editOrganizationCommand.enableElectricalProjectPlanning,
                                }}
                            >
                                {({ handleChange, state, name }) => (
                                    <>
                                        <SwitchField
                                            name={name}
                                            handleChange={handleChange}
                                            value={state.value}
                                            label={a('organisations.organisation.info.planningModule')}
                                        />
                                        <FieldInfo state={state} />
                                    </>
                                )}
                            </Field>
                            <Field
                                name="enableElectricalProjectRecommendations"
                                validators={{
                                    onChange:
                                        validators.editOrganizationCommand.enableElectricalProjectRecommendations,
                                }}
                            >
                                {({ handleChange, state, name }) => (
                                    <>
                                        <SwitchField
                                            name={name}
                                            handleChange={handleChange}
                                            value={state.value}
                                            label={a('organisations.organisation.info.recommendations')}
                                        />
                                        <FieldInfo state={state} />
                                    </>
                                )}
                            </Field>

                        </div>
                    )
                )}

            </Subscribe>
            <Button onClick={submitForm} disabled={organisationMutation.isPending}>
                Opslaan
            </Button>
        </div>
    );
}

function UserInfo() {
    const { t } = useTranslation('admin');
    const search = Route.useSearch();

    const { data } = useQuery(usersList(search));

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
                <Container.Title>{t('organisations.organisation.users.title')}</Container.Title>
                <UserModal organisationId={search.organisationId} user={null} />
            </div>
            {data?.Users?.map(user => (
                <User
                    key={user.UserId}
                    organisationId={search.organisationId}
                    user={user}
                />
            ))}
        </div>
    );
}

function User({
    organisationId,
    user,
}: {
    organisationId: string;
    user: components['schemas']['ListUserQueryResultUser'];
}) {
    const { t } = useTranslation('admin');
    const deleteUserMutation = api.useMutation(
        'delete',
        '/api/v2/users/delete',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(
                    usersList({ organisationId }),
                );
            },
        },
    );

    async function handleDelete({ userId }: { userId: string }) {
        if (await showConfirm(t('organisations.messages.confirmDeleteUser'))) {
            await deleteUserMutation.mutateAsync({
                params: {
                    query: {
                        UserId: userId,
                    },
                },
            });
        }
    }

    return (
        <div className="flex items-center justify-between">
            <h4 className="font-bold">{user?.DisplayName}</h4>
            <div className="flex items-center gap-4">
                <Button
                    variant="ghost"
                    onClick={async () =>
                        await handleDelete({
                            userId: user.UserId!,
                        })}
                >
                    <Trash2 />
                </Button>
                <UserModal organisationId={organisationId} user={user} />
            </div>
        </div>
    );
}

type User = {
    email: string;
    roleIds: string[];
    settings: {
        displayName: string;
        signatureText: string;
        firstName: string;
        middleName: string;
        lastName: string;
        department: string;
        jobTitleDescription: string;
        phoneNumber: string;
    };
};
function UserModal({
    organisationId,
    user,
}: {
    organisationId: string;
    user: components['schemas']['ListUserQueryResultUser'] | null;
}) {
    const { t } = useTranslation('applications');
    const { t: a } = useTranslation('admin');

    const [isOpen, setIsOpen] = useState(false);

    const search = Route.useSearch();

    const { data: rolesData } = useQuery(
        organisationRolesGet({ organisationId }),
    );

    const createUserMutation = api.useMutation(
        'post',
        '/api/v2/users/organizations/create',
        {
            onSuccess: () => {
                setIsOpen(false);
                queryClient.invalidateQueries(usersList(search));
            },
        },
    );

    const addRolesMutation = api.useMutation('put', '/api/v2/users/roles/add', {
        onSuccess: () => {
            queryClient.invalidateQueries(usersList(search));
        },
    });

    const removeRolesMutation = api.useMutation(
        'put',
        '/api/v2/users/roles/remove',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(usersList(search));
            },
        },
    );

    const defaultValues: User = {
        email: user?.Email || '',
        roleIds: user?.Roles?.map(role => role.RoleId!) || [],
        settings: {
            displayName: user?.DisplayName || '',
            signatureText: user?.Settings?.SignatureText || '',
            firstName: user?.Settings?.FirstName || '',
            middleName: user?.Settings?.MiddleName || '',
            lastName: user?.Settings?.LastName || '',
            department: user?.Settings?.Department || '',
            jobTitleDescription: user?.Settings?.JobTitleDescription || '',
            phoneNumber: user?.Settings?.PhoneNumber || '',
        },
    };

    const { Field, handleSubmit, reset, store } = useForm<User, ZodValidator>({
        defaultValues,
        validatorAdapter: zodValidator(),
        onSubmit: async ({ value }) => {
            if (!user) {
                await createUserMutation.mutateAsync({
                    body: {
                        OrganizationId: organisationId,
                        Email: value.email,
                        RoleIds: value.roleIds,
                        Settings: {
                            DisplayName: value.settings.displayName,
                            SignatureText: value.settings.signatureText,
                            FirstName: value.settings.firstName,
                            MiddleName: value.settings.middleName,
                            LastName: value.settings.lastName,
                            Department: value.settings.department,
                            JobTitleDescription:
                                value.settings.jobTitleDescription,
                            PhoneNumber: value.settings.phoneNumber,
                        },
                    },
                });
            }
            else {
                // check what roles are added and what roles are removed
                const addedRoles = value.roleIds.filter(
                    roleId =>
                        !user.Roles?.map(role => role.RoleId).includes(roleId),
                );
                const removedRoles
                    = user.Roles?.filter(
                        role => !value.roleIds.includes(role.RoleId!),
                    ).map(role => role.RoleId!) || [];

                let success = true;
                if (addedRoles.length > 0) {
                    const result = await addRolesMutation.mutateAsync({
                        body: {
                            UserId: user.UserId!,
                            RoleIds: addedRoles,
                        },
                    });
                    if (!result) {
                        success = false;
                    }
                }

                if (removedRoles.length > 0) {
                    const result = await removeRolesMutation.mutateAsync({
                        body: {
                            UserId: user.UserId!,
                            RoleIds: removedRoles,
                        },
                    });

                    if (!result) {
                        success = false;
                    }
                }

                if (success) {
                    setIsOpen(false);
                }
            }
        },
    });

    const isFormTouched = useFormTouched(store, defaultValues);

    async function handleClose() {
        if (isFormTouched) {
            if (await showConfirm('Weet je zeker dat je wilt sluiten?')) {
                setIsOpen(false);
                reset();
            }
        }
        else {
            setIsOpen(false);
            reset();
        }
    }

    const isButtonDisabled
        = createUserMutation.isPending
            || addRolesMutation.isPending
            || removeRolesMutation.isPending;
    const mutationErrors
        = createUserMutation.error
            || addRolesMutation.error
            || removeRolesMutation.error;

    return (
        <Popout open={isOpen} onOpenChange={setIsOpen}>
            <PopoutTrigger asChild>
                {user
                    ? (
                            <Button variant="ghost">
                                <Pen />
                            </Button>
                        )
                    : (
                            <Button>Gebruiker aanmaken</Button>
                        )}
            </PopoutTrigger>
            <PopoutContent>
                <PopoutHeader>
                    <PopoutTitle>
                        {user ? user.DisplayName : 'Gebruiker aanmaken'}
                    </PopoutTitle>
                </PopoutHeader>
                <PopoutBody>
                    <div className="flex flex-col gap-4 py-6">
                        <Field
                            name="email"
                            validators={{
                                onChange:
                                    validators.createOrganizationUserCommand.email,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label
                                    label={a('organisations.form.email.label')}
                                    className="col-span-5"
                                >
                                    <Input
                                        disabled={!!user}
                                        name={name}
                                        onChange={e =>
                                            handleChange(e.target.value)}
                                        value={state.value}
                                        placeholder={a('organisations.form.email.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>
                        <Field
                            name="settings.displayName"
                            validators={{
                                onChange:
                                    validators.createOrganizationUserCommand.createUserCommandSettings.displayName,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label
                                    label={a('organisations.organisation.users.displayName')}
                                    className="col-span-5"
                                >
                                    <Input
                                        disabled={!!user}
                                        name={name}
                                        onChange={e =>
                                            handleChange(e.target.value)}
                                        value={state.value}
                                        placeholder={a('organisations.organisation.users.displayName')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>

                        <Field name="roleIds">
                            {({ handleChange, state, name }) => (
                                <Label label={a('organisations.organisation.users.role')}>
                                    <MultipleOptionField
                                        name={name}
                                        handleChange={handleChange}
                                        state={state}
                                        render={MultipleOptionItem}
                                        options={
                                            rolesData?.Roles?.map(role => ({
                                                label: role.Name!,
                                                value: role.RoleId!,
                                            })) || []
                                        }
                                        dropdown
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>

                        {!user && (
                            <>
                                <Field
                                    name="settings.signatureText"
                                    validators={{
                                        onChange:
                                            validators.createOrganizationUserCommand.createUserCommandSettings.signatureText,
                                    }}
                                >
                                    {({ handleChange, state, name }) => (
                                        <Label
                                            label={a('organisations.organisation.users.signatureText')}
                                            className="col-span-5"
                                        >
                                            <Input
                                                disabled={!!user}
                                                name={name}
                                                onChange={e =>
                                                    handleChange(e.target.value)}
                                                value={state.value}
                                                placeholder={a('organisations.organisation.users.signatureText')}
                                            />
                                            <FieldInfo state={state} />
                                        </Label>
                                    )}
                                </Field>
                                <div className="grid grid-cols-7 gap-2">
                                    <Field
                                        name="settings.firstName"
                                        validators={{
                                            onChange:
                                                validators.createOrganizationUserCommand.createUserCommandSettings.firstName,
                                        }}
                                    >
                                        {({ handleChange, state, name }) => (
                                            <Label
                                                label={t(
                                                    'those-involved.client.form.contactpersons.modal.form.first_name.label',
                                                )}
                                                className="col-span-5"
                                            >
                                                <Input
                                                    name={name}
                                                    onChange={e =>
                                                        handleChange(
                                                            e.target.value,
                                                        )}
                                                    value={state.value}
                                                    placeholder={t(
                                                        'those-involved.client.form.contactpersons.modal.form.first_name.placeholder',
                                                    )}
                                                />
                                                <FieldInfo state={state} />
                                            </Label>
                                        )}
                                    </Field>

                                    <Field
                                        name="settings.middleName"
                                        validators={{
                                            onChange:
                                                validators.createOrganizationUserCommand.createUserCommandSettings.middleName,
                                        }}
                                    >
                                        {({ handleChange, state, name }) => (
                                            <Label
                                                label={t(
                                                    'those-involved.client.form.contactpersons.modal.form.infix.label',
                                                )}
                                                className="col-span-2"
                                            >
                                                <Input
                                                    name={name}
                                                    onChange={e =>
                                                        handleChange(
                                                            e.target.value,
                                                        )}
                                                    value={state.value}
                                                    placeholder={t(
                                                        'those-involved.client.form.contactpersons.modal.form.infix.placeholder',
                                                    )}
                                                />
                                                <FieldInfo state={state} />
                                            </Label>
                                        )}
                                    </Field>
                                </div>

                                <Field
                                    name="settings.lastName"
                                    validators={{
                                        onChange:
                                            validators.createOrganizationUserCommand.createUserCommandSettings.lastName,
                                    }}
                                >
                                    {({ handleChange, state, name }) => (
                                        <Label
                                            label={t(
                                                'those-involved.client.form.contactpersons.modal.form.last_name.label',
                                            )}
                                        >
                                            <Input
                                                name={name}
                                                onChange={e =>
                                                    handleChange(e.target.value)}
                                                value={state.value}
                                                placeholder={t(
                                                    'those-involved.client.form.contactpersons.modal.form.last_name.placeholder',
                                                )}
                                            />
                                            <FieldInfo state={state} />
                                        </Label>
                                    )}
                                </Field>

                                <Field
                                    name="settings.department"
                                    validators={{
                                        onChange:
                                            validators.createOrganizationUserCommand.createUserCommandSettings.department,
                                    }}
                                >
                                    {({ handleChange, state, name }) => (
                                        <Label
                                            label={t(
                                                'those-involved.client.form.contactpersons.modal.form.department.label',
                                            )}
                                        >
                                            <Input
                                                name={name}
                                                onChange={e =>
                                                    handleChange(e.target.value)}
                                                value={state.value}
                                                placeholder={t(
                                                    'those-involved.client.form.contactpersons.modal.form.department.placeholder',
                                                )}
                                            />
                                            <FieldInfo state={state} />
                                        </Label>
                                    )}
                                </Field>

                                <Field
                                    name="settings.jobTitleDescription"
                                    validators={{
                                        onChange:
                                            validators.createOrganizationUserCommand.createUserCommandSettings.jobTitleDescription,
                                    }}
                                >
                                    {({ handleChange, state, name }) => (
                                        <Label
                                            label={t(
                                                'those-involved.client.form.contactpersons.modal.form.role.label',
                                            )}
                                        >
                                            <Input
                                                name={name}
                                                onChange={e =>
                                                    handleChange(e.target.value)}
                                                value={state.value}
                                                placeholder={t(
                                                    'those-involved.client.form.contactpersons.modal.form.role.placeholder',
                                                )}
                                            />
                                            <FieldInfo state={state} />
                                        </Label>
                                    )}
                                </Field>

                                <Field
                                    name="settings.phoneNumber"
                                    validators={{
                                        onChange:
                                            validators.createOrganizationUserCommand.createUserCommandSettings.phoneNumber,
                                    }}
                                >
                                    {({ handleChange, state, name }) => (
                                        <Label
                                            label={t(
                                                'those-involved.client.form.contactpersons.modal.form.mobile_phonenumber.label',
                                            )}
                                        >
                                            <Input
                                                name={name}
                                                onChange={e =>
                                                    handleChange(e.target.value)}
                                                value={state.value}
                                                placeholder={t(
                                                    'those-involved.client.form.contactpersons.modal.form.mobile_phonenumber.placeholder',
                                                )}
                                            />
                                            <FieldInfo state={state} />
                                        </Label>
                                    )}
                                </Field>
                            </>
                        )}
                    </div>
                </PopoutBody>
                <PopoutFooter>
                    <div className="flex w-full flex-col gap-4">
                        <div className="flex w-full gap-4">
                            <Button
                                className="w-full"
                                onClick={handleClose}
                                variant="outline"
                                disabled={isButtonDisabled}
                            >
                                Annuleren
                            </Button>
                            <Button
                                className="w-full"
                                onClick={handleSubmit}
                                disabled={isButtonDisabled}
                            >
                                Opslaan
                            </Button>
                        </div>
                        {mutationErrors && (
                            <em className="text-red-500">
                                {mutationErrors?.Message}
                            </em>
                        )}
                    </div>
                </PopoutFooter>
            </PopoutContent>
        </Popout>
    );
}

function ListManagement() {
    const [list, setList] = useState<{
        value:
            | 'Normen'
            | 'Meetinstrumenten'
            | 'Checklist'
            | 'Constatering Teksten';
    }>({ value: 'Normen' });

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
                <Container.Title>Lijsten</Container.Title>
            </div>

            <SingleOptionField
                handleChange={(value) => {
                    setList({ value });
                }}
                name="listType"
                className="flex flex-row"
                options={[
                    { label: 'Normen', value: 'Normen' },
                    { label: 'Meetinstrumenten', value: 'Meetinstrumenten' },
                    { label: 'Checklist', value: 'Checklist' },
                    {
                        label: 'Constatering Teksten',
                        value: 'Constatering Teksten',
                    },
                ]}
                state={{ value: list.value }}
                render={SingleHorizontalOptionItemWithoutDot}
            />

            <Separator />

            {list.value === 'Normen' && <Normen />}

            {list.value === 'Meetinstrumenten' && <MeasurementDevices />}

            {list.value === 'Checklist' && <Checklist />}

            {list.value === 'Constatering Teksten' && <ConstateringTeksten />}
        </div>
    );
}

function Normen() {
    const { organisationId } = Route.useSearch();

    const { data } = useQuery(normsList({ organisationId }));

    const [selectedNormList, setSelectedNormList] = useState<string | null>(
        null,
    );

    useEffect(() => {
        if (data?.Count !== 0) {
            if (data?.Items && data.Items.length > 0) {
                const activeNormList = data.Items.find(item => item.Status === 'Active');
                if (activeNormList && activeNormList.ElectricalInspectionNormListId !== undefined) {
                    setSelectedNormList(activeNormList.ElectricalInspectionNormListId);
                }
            }
        }
    }, [data, selectedNormList]);

    return (
        <div className="flex flex-col gap-4">
            {data?.Count === 0
                ? (
                        <div className="flex items-center justify-between gap-4">
                            <p>Er zijn nog geen normenlijsten</p>
                            <NormList normId={selectedNormList} />
                        </div>
                    )
                : (
                        <div className="flex w-full flex-col gap-4">
                            <NormList normId={selectedNormList} />
                        </div>
                    )}
        </div>
    );
}

function NormList({ normId }: { normId: string | null }) {
    const getNormsQuery = useQuery(
        normsGet({ electricalInspectionNormListId: normId! }),
    );

    const { organisationId } = Route.useSearch();

    const organizationId = Route.useSearch().organisationId;

    const deleteNormMutation = api.useMutation(
        'delete',
        '/api/v2/electrical/inspectionnorms/items/delete',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(
                    normsGet({ electricalInspectionNormListId: normId! }),
                );
            },
        },
    );

    const createDefaultNormListMutation = api.useMutation(
        'post',
        '/api/v2/electrical/inspectionnorms/lists/createdefault',
        {
            onSuccess: (data) => {
                queryClient.invalidateQueries(
                    normsGet({
                        electricalInspectionNormListId: normId!,
                    }),
                );
                return data.ElectricalInspectionNormListId;
            },
        },
    );

    const activateDefaultNormListMutation = api.useMutation(
        'put',
        '/api/v2/electrical/inspectionnorms/lists/activate',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(
                    normsList({ organisationId }),
                );
            },
        },
    );

    const data = getNormsQuery.data;

    if (getNormsQuery.isLoading) {
        return <div>Loading</div>;
    }

    return (
        <>
            <Table>
                <Table.Header>
                    <Table.Row>
                        <Table.Cell>Norm</Table.Cell>
                        <Table.Cell>Naam</Table.Cell>
                        <Table.Cell>Omschrijving</Table.Cell>
                        <Table.Cell></Table.Cell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {data?.Items?.map(item => (
                        <Table.Row key={item.ElectricalInspectionNormItemId}>
                            <Table.Cell>{item.NormKind}</Table.Cell>
                            <Table.Cell>{item.Name}</Table.Cell>
                            <Table.Cell>{item.Description}</Table.Cell>
                            <Table.Cell>
                                <NormModal
                                    norm={item}
                                    normListId={normId!}
                                    norms={data}
                                />
                                <Button
                                    onClick={() => {
                                        deleteNormMutation.mutate({
                                            params: {
                                                query: {
                                                    ElectricalInspectionNormItemId: item.ElectricalInspectionNormItemId!,
                                                },
                                            },
                                        });
                                    }}
                                    variant="destructive"
                                >
                                    <Trash2 />
                                </Button>
                            </Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table>
            <NormModal norm={null} normListId={normId!} norms={data} />
            <Button
                variant="destructive"
                onClick={async () => {
                    const newListId = await createDefaultNormListMutation.mutateAsync({
                        body: {
                            OrganizationId: organizationId,
                            Description: 'Default lijst',
                        },
                    }).then(data => data.ElectricalInspectionNormListId);

                    activateDefaultNormListMutation.mutate({
                        body: {
                            ElectricalInspectionNormListId: newListId!,
                        },
                    });
                }}
            >
                Maak default lijst
            </Button>
        </>
    );
}

type NormFormValues = {
    name: string;
    description: string;
    isCustomInput: boolean;
    normKind: components['schemas']['ElectricalInspectionNormKind'];
    order: number;
};

function NormModal({
    norm,
    normListId,
}: {
    norm:
        | components['schemas']['ListElectricalInspectionNormItemQueryResultItem']
        | null;
    normListId: string;
    norms?: components['schemas']['ListElectricalInspectionNormItemQueryResult'];
}) {
    const { t } = useTranslation('admin');
    const { t: c } = useTranslation('common');
    const [isOpen, setIsOpen] = useState(false);

    const { organisationId } = Route.useSearch();

    const createNormListMutation = api.useMutation(
        'post',
        '/api/v2/electrical/inspectionnorms/lists/create',
        {
            onSuccess: (data) => {
                toast.success('Success');
                queryClient.invalidateQueries(
                    normsList({ organisationId }),
                );
                return data.ElectricalInspectionNormListId;
            },
        },
    );

    const createNormMutation = api.useMutation(
        'post',
        '/api/v2/electrical/inspectionnorms/items/create',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(
                    normsGet({ electricalInspectionNormListId: normListId }),
                );
            },
        },
    );

    const updateNormMutation = api.useMutation(
        'put',
        '/api/v2/electrical/inspectionnorms/items/edit',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(
                    normsGet({ electricalInspectionNormListId: normListId }),
                );
            },
        },
    );

    const activateNormListMutation = api.useMutation(
        'put',
        '/api/v2/electrical/inspectionnorms/lists/activate',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(normsList({ organisationId }));
                setIsOpen(false);
            },
        },
    );

    const defaultValues: NormFormValues = {
        name: norm?.Name || '',
        description: norm?.Description || '',
        isCustomInput: norm?.IsCustomInput || false,
        normKind: norm?.NormKind || 'SCIOS',
        order: norm?.Order || 999,
    };

    const { Field, handleSubmit, reset, store } = useForm<NormFormValues, ZodValidator>({
        defaultValues,
        validatorAdapter: zodValidator(),
        onSubmit: async ({ value }) => {
            if (norm === null) {
                if (normListId === null) {
                    normListId
                        = await createNormListMutation.mutateAsync({
                            body: {
                                OrganizationId: organisationId,
                                Description: 'normenlijst',
                            },
                        }).then(data => data.ElectricalInspectionNormListId);

                    activateNormListMutation.mutateAsync({
                        body: {
                            ElectricalInspectionNormListId:
                                normListId as string,
                        },
                    });
                }

                createNormMutation.mutate({
                    body: {
                        ElectricalInspectionNormListId:
                            normListId as string,
                        Description: value.description,
                        IsCustomInput: value.isCustomInput,
                        NormKind: value.normKind,
                        Order: value.order,
                    },
                });
            }
            else {
                updateNormMutation.mutateAsync({
                    body: {
                        ElectricalInspectionNormItemId: norm.ElectricalInspectionNormItemId as string,
                        Description: value.description,
                        IsCustomInput: value.isCustomInput,
                        NormKind: value.normKind,
                        Order: value.order,
                    },
                });
            }

            setIsOpen(false);
        },
    });

    const isFormTouched = useFormTouched(store, defaultValues);

    async function handleClose() {
        if (isFormTouched) {
            if (await showConfirm(c('messages.confirmClose'))) {
                setIsOpen(false);
                reset();
            }
        }
        else {
            setIsOpen(false);
            reset();
        }
    }

    return (
        <Popout open={isOpen} onOpenChange={setIsOpen}>
            <PopoutTrigger asChild>
                {norm
                    ? (
                            <Button variant="ghost">
                                <Pen />
                            </Button>
                        )
                    : (
                            <Button className="w-full">{t('organisation.checklistItems.norm.addButton')}</Button>
                        )}
            </PopoutTrigger>
            <PopoutContent>
                <PopoutHeader>
                    <PopoutTitle>{t('organisation.checklistItems.norm.title')}</PopoutTitle>
                </PopoutHeader>
                <PopoutBody>
                    <div className="flex flex-col gap-4 py-6">
                        <Field
                            name="name"
                            validators={{
                                onChange:
                                    validators.editElectricalInspectionNormItemCommand.name,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label label={t('organisation.checklistItems.name.label')}>
                                    <Input
                                        type="text"
                                        name={name}
                                        onChange={e =>
                                            handleChange(e.target.value)}
                                        value={state.value}
                                        placeholder={t('organisation.checklistItems.name.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>
                        <Field
                            name="description"
                            validators={{
                                onChange:
                                    validators.editElectricalInspectionNormItemCommand.description,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label label={t('organisation.checklistItems.description.label')}>
                                    <Input
                                        type="text"
                                        name={name}
                                        onChange={e =>
                                            handleChange(e.target.value)}
                                        value={state.value}
                                        placeholder={t('organisation.checklistItems.description.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>

                        <Field
                            name="normKind"
                            validators={{
                                onChange:
                                    validators.editElectricalInspectionNormItemCommand.normKind,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label label={t('organisation.checklistItems.norm.label')}>
                                    <SingleOptionField
                                        name={name}
                                        handleChange={handleChange}
                                        state={state}
                                        options={[
                                            {
                                                label: 'SCIOS',
                                                value: 'SCIOS',
                                            },
                                            {
                                                label: 'NEN',
                                                value: 'NEN',
                                            },
                                            {
                                                label: 'NPR',
                                                value: 'NPR',
                                            },
                                            {
                                                label: 'NTA',
                                                value: 'NTA',
                                            },
                                        ]}
                                        render={SingleOptionItem}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>
                    </div>
                </PopoutBody>
                <PopoutFooter>
                    <Button
                        className="w-full"
                        onClick={handleClose}
                        variant="outline"
                    >
                        {c('actions.general.cancel')}
                    </Button>
                    <Button
                        className="w-full"
                        onClick={handleSubmit}
                        disabled={
                            createNormMutation.isPending
                            || updateNormMutation.isPending
                        }
                    >
                        {c('actions.general.save')}
                    </Button>
                </PopoutFooter>
            </PopoutContent>
        </Popout>
    );
}

function Checklist() {
    const { organisationId } = Route.useSearch();

    const { data } = useQuery(checkList({ organisationId }));

    const [selectedCheckList, setSelectedCheckList] = useState<string | null>(
        null,
    );

    useEffect(() => {
        if (data?.Count !== 0) {
            if (data?.CheckLists && data.CheckLists.length > 0) {
                setSelectedCheckList(data.CheckLists[0].ElectricalCheckListId!);
            }
        }
    }, [data, selectedCheckList]);

    return (
        <div className="flex flex-col gap-4">
            {data?.Count === 0
                ? (
                        <div className="flex items-center justify-between gap-4">
                            <p>Er zijn nog geen checklijsten</p>
                            <CheckList checkListId={selectedCheckList} isActive={false} />
                        </div>
                    )
                : (
                        <div className="flex w-full flex-col gap-4">
                            <CheckList checkListId={selectedCheckList} isActive={data?.CheckLists[0]?.Status === 'Active'} />
                        </div>
                    )}
        </div>
    );
}

function CheckList({ checkListId }: { checkListId: string | null; isActive: boolean | undefined }) {
    const organizationId = Route.useSearch().organisationId;

    const deleteCheckListMutation = api.useMutation(
        'delete',
        '/api/v2/electrical/checklists/items/delete',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(checkListItemsGet({ electricalChecklistListId: checkListId! }));
            },
        },
    );

    const createDefaultCheckListMutation = api.useMutation(
        'post',
        '/api/v2/electrical/checklists/lists/createdefault',
        {
            onSuccess: (data) => {
                queryClient.invalidateQueries(
                    checkListItemsGet({ electricalChecklistListId: checkListId! }),
                );
                return data.ElectricalCheckListId;
            },
        },
    );

    const activateDefaultCheckListMutation = api.useMutation(
        'put',
        '/api/v2/electrical/checklists/lists/activate',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(
                    checkList({ organisationId: organizationId }),
                );
            },
        },
    );

    const getCheckListQuery = useQuery(
        checkListItemsGet({ electricalChecklistListId: checkListId! }),
    );

    const data = getCheckListQuery.data;

    const [openItemId, setOpenItemId] = useState<string | null>(null);

    if (getCheckListQuery.isLoading) {
        return <div>Loading</div>;
    }

    return (
        <div className="flex flex-col gap-2 rounded-md bg-background-tertiary p-4">
            {data?.Items?.map(item => (
                <div className="flex flex-col gap-4" key={item.ElectricalCheckListItemId}>
                    <div className="flex items-center justify-between">
                        <div>
                            <Button
                                variant="ghost"
                                onClick={() =>
                                    setOpenItemId(
                                        openItemId
                                        === item.ElectricalCheckListItemId
                                            ? null
                                            : (item.ElectricalCheckListItemId
                                                ?? null),
                                    )}
                            >
                                {openItemId
                                    === item.ElectricalCheckListItemId
                                    ? (
                                            <ArrowUp />
                                        )
                                    : (
                                            <ArrowDown />
                                        )}
                                {' '}
                            </Button>
                            <span>
                                {item?.Description}
                                {' '}
                                -
                                {item?.Kind}
                            </span>
                        </div>

                        <div>
                            <CheckListModal
                                checkListItem={item}
                                checkListId={checkListId!}
                            />
                            <Button
                                onClick={() => {
                                    deleteCheckListMutation.mutate({
                                        params: {
                                            query: {
                                                ElectricalCheckListItemId: item.ElectricalCheckListItemId!,
                                            },
                                        },
                                    });
                                }}
                                variant="destructive"
                            >
                                <Trash2 />
                            </Button>
                        </div>
                    </div>
                    <div className="flex flex-col gap-2">
                        {openItemId === item.ElectricalCheckListItemId && (
                            <CheckListText checkListItem={item} />
                        )}
                    </div>
                </div>
            ))}
            <CheckListModal
                checkListItem={null}
                checkListId={checkListId!}
            />

            <Button
                variant="destructive"
                onClick={async () => {
                    const checkListId = await createDefaultCheckListMutation.mutateAsync({
                        body: {
                            OrganizationId: organizationId,
                            Description: 'Default lijst',
                        },
                    }).then(data => data.ElectricalCheckListId);

                    activateDefaultCheckListMutation.mutate({
                        body: {
                            ElectricalCheckListId: checkListId!,
                        },
                    });
                }}
            >
                Maak default lijst
            </Button>

        </div>
    );
}

type CheckListItemTextFormValues = {
    text: string;
    kind: components['schemas']['ElectricalCheckListTextKind'];
    order: number;
};

function CheckListText({
    checkListItem,
}: {
    checkListItem: components['schemas']['ListElectricalCheckListItemQueryResultItem'];
}) {
    const getCheckListTextQuery = useQuery(
        checkListItemTextGet({
            electricalChecklistItemId: checkListItem.ElectricalCheckListItemId!,
        }),
    );

    const deleteCheckListTextMutation = api.useMutation(
        'delete',
        '/api/v2/electrical/checklists/texts/delete',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(
                    checkListItemTextGet({
                        electricalChecklistItemId: checkListItem.ElectricalCheckListItemId!,
                    }),
                );
            },
        },
    );

    const data = getCheckListTextQuery.data;

    if (getCheckListTextQuery.isLoading) {
        return <div>Loading</div>;
    }

    return (
        <div className="flex flex-col gap-2 rounded-md bg-background-tertiary p-4">
            {data?.Items?.map(item => (
                <div className="flex flex-col gap-4" key={item.ElectricalCheckListTextId}>
                    <div className="flex items-center justify-between">
                        <span>
                            {item?.Text}
                            {' '}
                            -
                            {item?.Kind}
                        </span>
                        <div>
                            <CheckListTextModal
                                checkListItemText={item}
                                CheckListItemId={
                                    checkListItem.ElectricalCheckListItemId!
                                }
                            />
                            <Button
                                onClick={() => {
                                    deleteCheckListTextMutation.mutate({
                                        params: {
                                            query: {
                                                ElectricalCheckListTextId: item.ElectricalCheckListTextId!,
                                            },
                                        },
                                    });
                                }}
                                variant="destructive"
                            >
                                <Trash2 />
                            </Button>
                        </div>
                    </div>
                </div>
            ))}
            <CheckListTextModal
                checkListItemText={null}
                CheckListItemId={checkListItem.ElectricalCheckListItemId!}
            />
        </div>
    );
}
function CheckListTextModal({
    checkListItemText,
    CheckListItemId,
}: {
    checkListItemText:
        | components['schemas']['ListElectricalCheckListTextQueryResultItem']
        | null;
    CheckListItemId: string;
}) {
    const { t } = useTranslation('admin');
    const { t: c } = useTranslation('common');
    const [isOpen, setIsOpen] = useState(false);

    const createCheckListTextMutation = api.useMutation(
        'post',
        '/api/v2/electrical/checklists/texts/create',
        {
            onSuccess: (data) => {
                toast.success('Success');
                queryClient.invalidateQueries(
                    checkListItemTextGet({
                        electricalChecklistItemId: CheckListItemId,
                    }),
                );
                setIsOpen(false);
                return data.ElectricalCheckListTextId;
            },
        },
    );

    const updateCheckListTextItemMutation = api.useMutation(
        'put',
        '/api/v2/electrical/checklists/texts/edit',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(
                    checkListItemTextGet({
                        electricalChecklistItemId: CheckListItemId,
                    }),
                );
                setIsOpen(false);
            },
        },
    );

    const defaultValues: CheckListItemTextFormValues = {
        text: checkListItemText?.Text || '',
        kind: checkListItemText?.Kind || 'Remark',
        order: checkListItemText?.Order || 999,
    };

    const { Field, handleSubmit, reset, store } = useForm<CheckListItemTextFormValues, ZodValidator>({
        defaultValues,
        validatorAdapter: zodValidator(),
        onSubmit: async ({ value }) => {
            if (checkListItemText === null) {
                createCheckListTextMutation.mutate({
                    body: {
                        Kind: value.kind,
                        Order: value.order,
                        Description: value.text,
                        ElectricalCheckListItemId: CheckListItemId,
                    },
                });
            }
            else {
                updateCheckListTextItemMutation.mutate({
                    body: {
                        ElectricalCheckListTextId:
                            checkListItemText.ElectricalCheckListTextId!,
                        Text: value.text,
                    },
                });
            }
        },
    });

    const isFormTouched = useFormTouched(store, defaultValues);

    async function handleClose() {
        if (isFormTouched) {
            if (await showConfirm(c('messages.confirmClose'))) {
                setIsOpen(false);
                reset();
            }
        }
        else {
            setIsOpen(false);
            reset();
        }
    }

    return (
        <Popout open={isOpen} onOpenChange={setIsOpen}>
            <PopoutTrigger asChild>
                {checkListItemText
                    ? (
                            <Button variant="ghost">
                                <Pen />
                            </Button>
                        )
                    : (
                            <Button className="w-full">
                                {t('organisation.checklistItems.checklistText.addButton')}
                            </Button>
                        )}
            </PopoutTrigger>
            <PopoutContent>
                <PopoutHeader>
                    <PopoutTitle>{t('organisation.checklistItems.checklistText.title')}</PopoutTitle>
                </PopoutHeader>
                <PopoutBody>
                    <div className="flex flex-col gap-4 py-6">
                        <Field
                            name="text"
                            validators={{
                                onChange:
                                    validators.createElectricalCheckListTextCommand.description,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label label={t('organisation.checklistItems.checklistText.text.label')}>
                                    <Input
                                        type="text"
                                        name={name}
                                        onChange={e =>
                                            handleChange(e.target.value)}
                                        value={state.value}
                                        placeholder={t('organisation.checklistItems.checklistText.text.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>
                        {!checkListItemText && (
                            <Field
                                name="kind"
                                validators={{
                                    onChange:
                                        validators.createElectricalCheckListTextCommand.kind,
                                }}
                            >
                                {({ handleChange, state, name }) => (
                                    <Label label={t('organisation.checklistItems.checklistText.type.label')}>
                                        <SingleOptionField
                                            name={name}
                                            handleChange={handleChange}
                                            state={state}
                                            options={[
                                                {
                                                    label: 'Remark',
                                                    value: 'Remark',
                                                },
                                                {
                                                    label: 'Recommendation',
                                                    value: 'Recommendation',
                                                },
                                            ]}
                                            render={SingleOptionItem}
                                        />
                                        <FieldInfo state={state} />
                                    </Label>
                                )}
                            </Field>
                        )}
                    </div>
                </PopoutBody>
                <PopoutFooter>
                    <Button
                        className="w-full"
                        onClick={handleClose}
                        variant="outline"
                    >
                        {c('actions.general.cancel')}
                    </Button>
                    <Button
                        className="w-full"
                        onClick={handleSubmit}
                        disabled={
                            createCheckListTextMutation.isPending
                            || updateCheckListTextItemMutation.isPending
                        }
                    >
                        {c('actions.general.save')}
                    </Button>
                </PopoutFooter>
            </PopoutContent>
        </Popout>
    );
}

type CheckListFormValues = {
    description: string;
    kind: components['schemas']['ElectricalCheckListItemKind'];
    order: number;
};

function CheckListModal({
    checkListItem,
    checkListId,
}: {
    checkListItem:
        | components['schemas']['ListElectricalCheckListItemQueryResultItem']
        | null;
    checkListId: string;
}) {
    const [isOpen, setIsOpen] = useState(false);

    const { organisationId } = Route.useSearch();

    const createCheckListItemMutation = api.useMutation(
        'post',
        '/api/v2/electrical/checklists/items/create',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(
                    checkListItemsGet({
                        electricalChecklistListId: checkListId,
                    }),
                );
            },
        },
    );

    const updateCheckListItemMutation = api.useMutation(
        'put',
        '/api/v2/electrical/checklists/items/edit',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(
                    checkListItemsGet({
                        electricalChecklistListId: checkListId,
                    }),
                );
            },
        },
    );

    const activateCheckListMutation = api.useMutation(
        'put',
        '/api/v2/electrical/checklists/lists/activate',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(checkList({ organisationId }));
                setIsOpen(false);
            },
        },
    );

    const defaultValues: CheckListFormValues = {
        description: checkListItem?.Description || '',
        kind: checkListItem?.Kind || 'DistributionBoard',
        order: checkListItem?.Order || 999,
    };

    const { Field, handleSubmit, reset, store } = useForm<CheckListFormValues, ZodValidator>({
        defaultValues,
        validatorAdapter: zodValidator(),
        onSubmit: async ({ value }) => {
            if (checkListItem === null) {
                createCheckListItemMutation.mutate({
                    body: {
                        ElectricalCheckListId: checkListId,
                        Description: value.description,
                        Kind: value.kind,
                        Order: value.order,
                    },
                });

                activateCheckListMutation.mutateAsync({
                    body: {
                        ElectricalCheckListId: checkListId,
                    },
                });
            }
            else {
                updateCheckListItemMutation.mutate({
                    body: {
                        ElectricalCheckListItemId: checkListItem.ElectricalCheckListItemId!,
                        Description: value.description,
                        Order: value.order,
                    },
                });

                activateCheckListMutation.mutateAsync({
                    body: {
                        ElectricalCheckListId: checkListId,
                    },
                });
            }
        },
    });

    const isFormTouched = useFormTouched(store, defaultValues);

    async function handleClose() {
        if (isFormTouched) {
            if (await showConfirm('Weet je zeker dat je wilt sluiten?')) {
                setIsOpen(false);
                reset();
            }
        }
        else {
            setIsOpen(false);
            reset();
        }
    }

    return (
        <Popout open={isOpen} onOpenChange={setIsOpen}>
            <PopoutTrigger asChild>
                {checkListItem
                    ? (
                            <Button variant="ghost">
                                <Pen />
                            </Button>
                        )
                    : (
                            <Button className="w-full">
                                Nieuwe checklist item toevoegen
                            </Button>
                        )}
            </PopoutTrigger>
            <PopoutContent>
                <PopoutHeader>
                    <PopoutTitle>Checklist item</PopoutTitle>
                </PopoutHeader>
                <PopoutBody>
                    <div className="flex flex-col gap-4 py-6">
                        <Field
                            name="description"
                            validators={{
                                onChange:
                                    validators.editElectricalInspectionNormItemCommand.description,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label label="Naam">
                                    <Input
                                        type="text"
                                        name={name}
                                        onChange={e =>
                                            handleChange(e.target.value)}
                                        value={state.value}
                                        placeholder="Noteer naam"
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>

                        <Field
                            name="kind"
                            validators={{
                                onChange:
                                    validators.createElectricalCheckListItemCommand.kind,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label label="Type">
                                    <SingleOptionField
                                        name={name}
                                        handleChange={handleChange}
                                        state={state}
                                        options={[
                                            {
                                                label: 'Distribution Board',
                                                value: 'DistributionBoard',
                                            },
                                            {
                                                label: 'Final Circuit',
                                                value: 'FinalCircuit',
                                            },
                                            {
                                                label: 'Photovoltaic Inverter',
                                                value: 'PhotovoltaicInverter',
                                            },
                                            {
                                                label: 'Other',
                                                value: 'Other',
                                            },
                                        ]}
                                        render={SingleOptionItem}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>
                    </div>
                </PopoutBody>
                <PopoutFooter>
                    <Button
                        className="w-full"
                        onClick={handleClose}
                        variant="outline"
                    >
                        Annuleren
                    </Button>
                    <Button
                        className="w-full"
                        onClick={handleSubmit}
                        disabled={
                            createCheckListItemMutation.isPending
                            || updateCheckListItemMutation.isPending
                        }
                    >
                        Opslaan
                    </Button>
                </PopoutFooter>
            </PopoutContent>
        </Popout>
    );
}

function ConstateringTeksten() {
    const { organisationId } = Route.useSearch();

    const { data } = useQuery(observationStandardTexts({ organisationId }));

    const [selectedObservationText, setSelectedObservationText] = useState<
        | components['schemas']['ListElectricalObservationStandardTextItemQueryResultItem'][]
        | null
    >(null);

    const deleteStandardTextMutation = api.useMutation(
        'delete',
        '/api/v2/electrical/observationstandardtexts/items/delete',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(
                    observationStandardTexts({ organisationId }),
                );
            },
        },
    );

    const organizationId = Route.useSearch().organisationId;

    const createDefaultObservationTextMutation = api.useMutation(
        'post',
        '/api/v2/electrical/observationstandardtexts/items/createdefault',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(
                    observationStandardTexts({ organisationId }),
                );
            },
        },
    );

    useEffect(() => {
        if (data?.Count !== 0) {
            if (data?.Items && data.Items.length > 0) {
                setSelectedObservationText(data.Items);
            }
        }
    }, [data, selectedObservationText]);

    return (
        <div className="flex flex-col gap-4">
            {data?.Count === 0
                ? (
                        <div className="flex items-center justify-between gap-4">
                            <p>Er zijn nog geen observatie teksten</p>
                            <ObservationTextModal ObservationText={null} />
                        </div>
                    )
                : (
                        <div className="flex flex-col gap-2 rounded-md bg-background-tertiary p-4">
                            <div className="flex w-full flex-col gap-4">
                                {selectedObservationText?.map(item => (
                                    <div key={item.ElectricalObservationStandardTextItemId} className="flex flex-col gap-4">
                                        <div className="flex items-center justify-between">
                                            <p>
                                                {item?.Description}
                                                {' '}
                                                -
                                                {item?.ObjectKind}
                                            </p>
                                            <div>
                                                <ObservationTextModal
                                                    ObservationText={item}
                                                />
                                                <Button
                                                    onClick={() => {
                                                        deleteStandardTextMutation.mutate({
                                                            params: {
                                                                query: {
                                                                    ElectricalObservationStandardTextItemId: item.ElectricalObservationStandardTextItemId!,
                                                                },
                                                            },
                                                        });
                                                    }}
                                                    variant="destructive"
                                                >
                                                    <Trash2 />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <ObservationTextModal ObservationText={null} />
                            <Button
                                variant="destructive"
                                onClick={async () => {
                                    createDefaultObservationTextMutation.mutate({
                                        body: {
                                            OrganizationId: organizationId,
                                        },
                                    });
                                }}
                            >
                                Maak default lijst
                            </Button>
                        </div>
                    )}
        </div>
    );
}

type StandardTextFormValues = {
    objectKind: components['schemas']['ElectricalObservationStandardObjectKind'];
    textKind: components['schemas']['ElectricalObservationStandardTextItemKind'];
    description: string;
    order: number;
};

function ObservationTextModal({
    ObservationText,
}: {
    ObservationText:
        | components['schemas']['ListElectricalObservationStandardTextItemQueryResultItem']
        | null;
}) {
    const [isOpen, setIsOpen] = useState(false);

    const { organisationId } = Route.useSearch();

    const createStandardTextMutation = api.useMutation(
        'post',
        '/api/v2/electrical/observationstandardtexts/items/create',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(
                    observationStandardTexts({ organisationId }),
                );
                setIsOpen(false);
            },
        },
    );

    const updateStandardTextMutation = api.useMutation(
        'put',
        '/api/v2/electrical/observationstandardtexts/items/edit',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(
                    observationStandardTexts({ organisationId }),
                );
                setIsOpen(false);
            },
        },
    );

    const defaultValues: StandardTextFormValues = {
        objectKind: ObservationText?.ObjectKind || 'DistributionBoard',
        textKind: ObservationText?.TextKind || 'Recommendation',
        description: ObservationText?.Description || '',
        order: ObservationText?.Order || 999,
    };

    const { Field, handleSubmit, reset, store } = useForm<StandardTextFormValues, ZodValidator>({
        defaultValues,
        validatorAdapter: zodValidator(),
        onSubmit: async ({ value }) => {
            if (ObservationText === null) {
                createStandardTextMutation.mutate({
                    body: {
                        ObjectKind: value.objectKind,
                        TextKind: value.textKind,
                        Description: value.description,
                        OrganizationId: organisationId,
                        Order: value.order,
                    },
                });
            }
            else {
                updateStandardTextMutation.mutateAsync({
                    body: {
                        ElectricalObservationStandardTextItemId:
                            ObservationText.ElectricalObservationStandardTextItemId!,
                        Description: value.description,
                        Order: value.order,
                    },
                });
            }
        },
    });

    const isFormTouched = useFormTouched(store, defaultValues);

    async function handleClose() {
        if (isFormTouched) {
            if (await showConfirm('Weet je zeker dat je wilt sluiten?')) {
                setIsOpen(false);
                reset();
            }
        }
        else {
            setIsOpen(false);
            reset();
        }
    }

    return (
        <Popout open={isOpen} onOpenChange={setIsOpen}>
            <PopoutTrigger asChild>
                {ObservationText
                    ? (
                            <Button variant="ghost">
                                <Pen />
                            </Button>
                        )
                    : (
                            <Button className="w-full">
                                Nieuwe checklist item toevoegen
                            </Button>
                        )}
            </PopoutTrigger>
            <PopoutContent>
                <PopoutHeader>
                    <PopoutTitle>checklist item</PopoutTitle>
                </PopoutHeader>
                <PopoutBody>
                    <div className="flex flex-col gap-4 py-6">
                        <Field
                            name="description"
                            validators={{
                                onChange:
                                    validators.createElectricalObservationStandardTextItemCommand.description,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label label="Naam">
                                    <Input
                                        type="text"
                                        name={name}
                                        onChange={e =>
                                            handleChange(e.target.value)}
                                        value={state.value}
                                        placeholder="Noteer naam"
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>

                        {!ObservationText && (
                            <Field
                                name="objectKind"
                                validators={{
                                    onChange:
                                        validators.createElectricalObservationStandardTextItemCommand.objectKind,
                                }}
                            >
                                {({ handleChange, state, name }) => (
                                    <Label label="object kind">
                                        <SingleOptionField
                                            name={name}
                                            handleChange={handleChange}
                                            state={state}
                                            options={[
                                                {
                                                    label: 'Distribution Board',
                                                    value: 'DistributionBoard',
                                                },
                                                {
                                                    label: 'Final Circuit',
                                                    value: 'FinalCircuit',
                                                },
                                                {
                                                    label: 'Distribution Board File',
                                                    value: 'DistributionBoardFile',
                                                },
                                                {
                                                    label: 'Distribution Board Device',
                                                    value: 'DistributionBoardDevice',
                                                },

                                            ]}
                                            render={SingleOptionItem}
                                        />
                                        <FieldInfo state={state} />
                                    </Label>
                                )}
                            </Field>
                        )}

                        {!ObservationText && (
                            <Field
                                name="textKind"
                                validators={{
                                    onChange:
                                        validators.createElectricalObservationStandardTextItemCommand.textKind,
                                }}
                            >
                                {({ handleChange, state, name }) => (
                                    <Label label="text kind">
                                        <SingleOptionField
                                            name={name}
                                            handleChange={handleChange}
                                            state={state}
                                            options={[
                                                {
                                                    label: 'Recommendation',
                                                    value: 'Recommendation',
                                                },
                                                {
                                                    label: 'Remark',
                                                    value: 'Remark',
                                                },
                                            ]}
                                            render={SingleOptionItem}
                                        />
                                        <FieldInfo state={state} />
                                    </Label>
                                )}
                            </Field>
                        )}
                    </div>
                </PopoutBody>
                <PopoutFooter>
                    <Button
                        className="w-full"
                        onClick={handleClose}
                        variant="outline"
                    >
                        Annuleren
                    </Button>
                    <Button
                        className="w-full"
                        onClick={handleSubmit}
                        disabled={
                            createStandardTextMutation.isPending
                            || updateStandardTextMutation.isPending
                        }
                    >
                        Opslaan
                    </Button>
                </PopoutFooter>
            </PopoutContent>
        </Popout>
    );
}

function MeasurementDevices() {
    const { organisationId } = Route.useSearch();

    const { data } = useQuery(measurementDevices({ organisationId }));
    const deleteMeasurementDevice = api.useMutation(
        'delete',
        '/api/v2/electrical/measurementdevices/delete',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(
                    measurementDevices({ organisationId }),
                );
            },
        },
    );

    const [selectedMeasurementDevice, setSelectedMeasurementDevice] = useState<string>('');

    return (
        <div className="flex flex-col gap-2 rounded-md bg-background-tertiary p-4">
            {data?.Devices?.map(item => (
                <div className="flex flex-col gap-4" key={item.ElectricalMeasurementDeviceId}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Button variant="ghost" onClick={() => setSelectedMeasurementDevice(selectedMeasurementDevice === item.ElectricalMeasurementDeviceId ? '' : item.ElectricalMeasurementDeviceId!)}>
                                {selectedMeasurementDevice === item.ElectricalMeasurementDeviceId ? <ArrowUp /> : <ArrowDown />}
                            </Button>
                            <p>
                                {item?.Name}
                                {' '}
                                -
                                {item?.BrandName}
                                {' '}
                                -
                                {item?.DeviceType}
                                {' '}
                                -
                                {item?.SerialNumber}
                                {' '}
                                -
                                {item?.CalibrationKind}
                            </p>
                        </div>
                        <div>
                            <MeasurementDeviceModal measurementDevice={item} />
                            <Button variant="destructive" onClick={() => deleteMeasurementDevice.mutate({ params: { query: { ElectricalMeasurementDeviceId: item.ElectricalMeasurementDeviceId! } } })}>
                                <Trash2 />
                            </Button>
                        </div>
                    </div>
                    {selectedMeasurementDevice === item.ElectricalMeasurementDeviceId && <MeasurementDeviceCalibations measurementDeviceId={item.ElectricalMeasurementDeviceId} />}
                </div>
            ))}
            <MeasurementDeviceModal measurementDevice={null} />
        </div>
    );
}

function MeasurementDeviceCalibations({ measurementDeviceId }: { measurementDeviceId: string }) {
    const { data } = useQuery(
        measurementDevicesCalibrations({ measurementDeviceId }),
    );

    return (
        <div className="flex flex-col gap-4">

            {data?.Calibrations?.map(item => (
                <div className="flex flex-col gap-4" key={item.ElectricalMeasurementDeviceCalibrationId}>
                    <div className="flex items-center justify-between">
                        <span>
                            {item?.CertificateNumber}
                            :
                            {item?.StartDate}
                            {' '}
                            -
                            {item?.EndDate}
                        </span>
                    </div>
                </div>
            ))}

            <MeasurementDeviceCalibrationModal measurementDeviceId={measurementDeviceId} />
        </div>
    );
}

type MeasurementDeviceCalibrationFormValues = {
    CertificateNumber: string;
    ElectricalMeasurementDeviceId: string;
    EndDate: Date;
    StartDate: Date;
};

function MeasurementDeviceCalibrationModal({ measurementDeviceId }: { measurementDeviceId: string }) {
    const [isOpen, setIsOpen] = useState(false);

    const createMeasurementDeviceCalibration = api.useMutation(
        'post',
        '/api/v2/electrical/measurementdevices/calibrations/create',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(
                    measurementDevicesCalibrations({ measurementDeviceId }),
                );
                setIsOpen(false);
            },
        },
    );

    const { Field, handleSubmit } = useForm<MeasurementDeviceCalibrationFormValues, ZodValidator>({
        defaultValues: {
            CertificateNumber: '',
            ElectricalMeasurementDeviceId: measurementDeviceId,
            StartDate: new Date(),
            EndDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)),
        },
        validatorAdapter: zodValidator(),
        onSubmit: async ({ value }) => {
            createMeasurementDeviceCalibration.mutate({
                body: {
                    CertificateNumber: value.CertificateNumber,
                    ElectricalMeasurementDeviceId: value.ElectricalMeasurementDeviceId,
                    EndDate: value.EndDate.toDateString(),
                    StartDate: value.StartDate.toDateString(),
                },
            });
        },
    });

    return (
        <Popout open={isOpen} onOpenChange={setIsOpen}>
            <PopoutTrigger asChild>
                <Button className="w-full">
                    Nieuwe kalibratie toevoegen
                </Button>
            </PopoutTrigger>
            <PopoutContent>
                <PopoutHeader>
                    <PopoutTitle>Kalibratie</PopoutTitle>
                </PopoutHeader>
                <PopoutBody>
                    <div className="flex flex-col gap-4 py-6">
                        <Field
                            name="CertificateNumber"
                            validators={{
                                onChange:
                                    validators.createElectricalMeasurementDeviceCalibrationCommand.certificateNumber,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label label="Certificaat nummer">
                                    <Input
                                        type="text"
                                        name={name}
                                        onChange={e =>
                                            handleChange(e.target.value)}
                                        value={state.value}
                                        placeholder="Noteer certificaat nummer"
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>

                        <Field
                            name="StartDate"
                            validators={{
                                onChange:
                                    validators.createElectricalMeasurementDeviceCalibrationCommand.startDate,
                            }}
                        >
                            {({ handleChange, state }) => (
                                <Label label="Certificaat start datum">
                                    <DateField
                                        onChange={handleChange}
                                        value={state.value}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>

                        <Field
                            name="EndDate"
                            validators={{
                                onChange:
                                    validators.createElectricalMeasurementDeviceCalibrationCommand.endDate,
                            }}
                        >
                            {({ handleChange, state }) => (
                                <Label label="Certificaat end datum">
                                    <DateField
                                        onChange={handleChange}
                                        value={state.value}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>

                    </div>
                </PopoutBody>
                <PopoutFooter>
                    <Button
                        className="w-full"
                        onClick={() => {
                            handleSubmit();
                            setIsOpen(false);
                        }}
                    >
                        Opslaan
                    </Button>
                </PopoutFooter>
            </PopoutContent>
        </Popout>
    );
}

type MeasurementDeviceFormValues = {
    name: string;
    brandName: string;
    deviceType: string;
    serialNumber: string;
    calibrationKind:
        | components['schemas']['ElectricalMeasurementDeviceCalibrationKind']
        | null;
    userId: string;
};

function MeasurementDeviceModal({
    measurementDevice,
}: {
    measurementDevice:
        | components['schemas']['ListElectricalMeasurementDeviceQueryResultDevice']
        | null;
}) {
    const [isOpen, setIsOpen] = useState(false);

    const { organisationId } = Route.useSearch();

    const { data } = useQuery(measurementDevicesCreate({ organisationId }));

    const userList = useMemo<
        components['schemas']['ListUserQueryResultUser'][] | null
    >(() => data?.Users ?? null, [data]);

    const createMeasurementDevice = api.useMutation(
        'post',
        '/api/v2/electrical/measurementdevices/create',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(
                    measurementDevices({ organisationId }),
                );
                setIsOpen(false);
            },
        },
    );

    const updateMeasurementDevice = api.useMutation(
        'put',
        '/api/v2/electrical/measurementdevices/edit',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(
                    measurementDevices({ organisationId }),
                );
                setIsOpen(false);
            },
        },
    );

    const defaultValues: MeasurementDeviceFormValues = {
        brandName: measurementDevice?.BrandName || '',
        deviceType: measurementDevice?.DeviceType || '',
        serialNumber: measurementDevice?.SerialNumber || '',
        calibrationKind: measurementDevice?.CalibrationKind || 'Optional',
        name: measurementDevice?.Name || '',
        userId: measurementDevice?.UserId || '',
    };

    const { Field, handleSubmit, reset, store } = useForm<MeasurementDeviceFormValues, ZodValidator>({
        defaultValues,
        validatorAdapter: zodValidator(),
        onSubmit: async ({ value }) => {
            // create
            if (measurementDevice === null) {
                createMeasurementDevice.mutate({
                    body: {
                        Name: value.name,
                        BrandName: value.brandName,
                        OrganizationId: organisationId,
                        DeviceType: value.deviceType,
                        SerialNumber: value.serialNumber,
                        CalibrationKind:
                            value.calibrationKind || 'NotApplicable',
                        UserId: value.userId,
                    },
                });

                // update
            }
            else {
                updateMeasurementDevice.mutate({
                    body: {
                        ElectricalMeasurementDeviceId:
                            measurementDevice.ElectricalMeasurementDeviceId!,
                        Name: value.name,
                        UserId: value.userId,
                    },
                });
            }
        },
    });

    const isFormTouched = useFormTouched(store, defaultValues);

    async function handleClose() {
        if (isFormTouched) {
            if (await showConfirm('Weet je zeker dat je wilt sluiten?')) {
                setIsOpen(false);
                reset();
            }
        }
        else {
            setIsOpen(false);
            reset();
        }
    }

    return (
        <>
            {userList && userList.length > 0
                ? (
                        <Popout open={isOpen} onOpenChange={setIsOpen}>
                            <PopoutTrigger asChild>

                                {measurementDevice
                                    ? (
                                            <Button variant="ghost">
                                                <Pen />
                                            </Button>
                                        )
                                    : (
                                            <Button className="w-full">
                                                Nieuwe meetinstrument toevoegen
                                            </Button>
                                        )}

                            </PopoutTrigger>
                            <PopoutContent>
                                <PopoutHeader>
                                    <PopoutTitle>Meetinstrument</PopoutTitle>
                                </PopoutHeader>
                                <PopoutBody>
                                    <div className="flex flex-col gap-4 py-6">
                                        <Field
                                            name="name"
                                            validators={{
                                                onChange:
                                                validators.createElectricalMeasurementDeviceCommand.name,
                                            }}
                                        >
                                            {({ handleChange, state, name }) => (
                                                <Label label="Naam">
                                                    <Input
                                                        type="text"
                                                        name={name}
                                                        onChange={e =>
                                                            handleChange(e.target.value)}
                                                        value={state.value}
                                                        placeholder="Noteer naam"
                                                    />
                                                    <FieldInfo state={state} />
                                                </Label>
                                            )}
                                        </Field>

                                        <Field
                                            name="brandName"
                                            validators={{
                                                onChange:
                                                validators.createElectricalMeasurementDeviceCommand.brandName,
                                            }}
                                        >
                                            {({ handleChange, state, name }) => (
                                                <Label label="Merknaam">
                                                    <Input
                                                        type="text"
                                                        name={name}
                                                        onChange={e =>
                                                            handleChange(e.target.value)}
                                                        value={state.value}
                                                        placeholder="Noteer merknaam"
                                                    />
                                                    <FieldInfo state={state} />
                                                </Label>
                                            )}
                                        </Field>

                                        <Field
                                            name="deviceType"
                                            validators={{
                                                onChange:
                                                validators.createElectricalMeasurementDeviceCommand.deviceType,
                                            }}
                                        >
                                            {({ handleChange, state, name }) => (
                                                <Label label="Type apparaat">
                                                    <Input
                                                        type="text"
                                                        name={name}
                                                        onChange={e =>
                                                            handleChange(e.target.value)}
                                                        value={state.value}
                                                        placeholder="Noteer type apparaat"
                                                    />
                                                    <FieldInfo state={state} />
                                                </Label>
                                            )}
                                        </Field>

                                        <Field
                                            name="serialNumber"
                                            validators={{
                                                onChange:
                                                validators.createElectricalMeasurementDeviceCommand.serialNumber,
                                            }}
                                        >
                                            {({ handleChange, state, name }) => (
                                                <Label label="Serienummer">
                                                    <Input
                                                        type="text"
                                                        name={name}
                                                        onChange={e =>
                                                            handleChange(e.target.value)}
                                                        value={state.value}
                                                        placeholder="Noteer serienummer"
                                                    />
                                                    <FieldInfo state={state} />
                                                </Label>
                                            )}
                                        </Field>

                                        <Field
                                            name="calibrationKind"
                                            validators={{
                                                onChange:
                                                validators.createElectricalMeasurementDeviceCommand.calibrationKind,
                                            }}
                                        >
                                            {({ handleChange, state, name }) => (
                                                <Label label="Kalibratie soort">
                                                    <SingleOptionField
                                                        name={name}
                                                        handleChange={handleChange}
                                                        state={state}
                                                        options={[
                                                            {
                                                                label: 'Not Applicable',
                                                                value: 'NotApplicable',
                                                            },
                                                            {
                                                                label: 'Optional',
                                                                value: 'Optional',
                                                            },
                                                            {
                                                                label: 'Required',
                                                                value: 'Required',
                                                            },
                                                        ]}
                                                        render={SingleOptionItem}
                                                    />
                                                    <FieldInfo state={state} />
                                                </Label>
                                            )}
                                        </Field>

                                        <Field
                                            name="userId"
                                            validators={{
                                                onChange:
                                                validators.createElectricalMeasurementDeviceCommand.userId,
                                            }}
                                        >
                                            {({ handleChange, state, name }) => (
                                                <Label label="Gebruiker">
                                                    <SingleOptionField
                                                        name={name}
                                                        handleChange={handleChange}
                                                        state={state}
                                                        options={userList!.map(
                                                            (
                                                                item: components['schemas']['ListUserQueryResultUser'],
                                                            ) => ({
                                                                label: item.DisplayName ?? '',
                                                                value: item.UserId!,
                                                            }),
                                                        )}
                                                        render={SingleOptionItem}
                                                    />
                                                    <FieldInfo state={state} />
                                                </Label>
                                            )}
                                        </Field>
                                    </div>
                                </PopoutBody>
                                <PopoutFooter>
                                    <Button
                                        className="w-full"
                                        onClick={handleClose}
                                        variant="outline"
                                    >
                                        Annuleren
                                    </Button>
                                    <Button className="w-full" onClick={handleSubmit}>
                                        Opslaan
                                    </Button>
                                </PopoutFooter>
                            </PopoutContent>
                        </Popout>
                    )
                : (
                        <p>Geen gebruikers beschikbaar om toe te wijzen</p>
                    )}
        </>
    );
}
